import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const videoId = id ? parseInt(id, 10) : 0;
  const video = getVideoById(videoId);
  const relatedVideos = video ? getRelatedVideos(video) : [];
  const category = video ? categories.find((c) => c.slug === video.category) : undefined;

  if (!video) {
    return (
      <>
        <SEOMeta title="视频未找到" description="抱歉，您访问的视频页面不存在。" />
        <Navbar />
        <main className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 pt-16">
          <div className="w-20 h-20 flex items-center justify-center mb-6">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">
            视频未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您访问的视频页面不存在或已下架。</p>
          <Link
            to="/"
            className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOMeta
        title={`${video.title} - 星空传媒在线观看`}
        description={`${video.title}，${video.description.substring(0, 120)}。星空传媒高清资源免费在线播放，画质清晰流畅。`}
        keywords={`${video.title},星空传媒在线免费观看,${video.category},星空传媒高清资源`}
        canonicalUrl={`https://sportzup.github.io/article/${video.id}`}
        ogTitle={`${video.title} - 星空传媒在线观看`}
        ogDescription={`${video.title}，${video.description.substring(0, 120)}。星空传媒高清资源免费在线播放。`}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category?.name || video.category, href: `/category/${video.category}` },
            { label: video.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="relative rounded-xl overflow-hidden aspect-video bg-background-950 mb-6">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                    title={`${video.title} - 星空传媒高清资源`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-950/50 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center justify-between">
                    <span className="px-3 py-1 text-sm font-medium rounded-md bg-background-950/60 text-background-50 backdrop-blur-sm whitespace-nowrap">
                      {video.duration}
                    </span>
                    <button className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-fill"></i>
                      </div>
                      立即播放
                    </button>
                  </div>
                </div>

                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                  {video.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-900">{video.rating}</span>
                  </div>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className="text-sm text-foreground-500">{video.views}次播放</span>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className="text-sm text-foreground-500">{video.year}</span>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className="text-sm text-foreground-500">{video.updateInfo}</span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-6">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-2">
                    剧情简介
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                </div>

                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-3">
                    影片信息
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-xs text-foreground-500">导演</span>
                      <p className="text-sm text-foreground-800 mt-0.5">{video.director}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-500">主演</span>
                      <p className="text-sm text-foreground-800 mt-0.5">
                        {video.cast.join(' / ')}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-500">年份</span>
                      <p className="text-sm text-foreground-800 mt-0.5">{video.year}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-500">分类</span>
                      <p className="text-sm text-foreground-800 mt-0.5">
                        {category?.name || video.category}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="sticky top-20">
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
                    相关推荐
                  </h3>
                  <div className="space-y-3">
                    {relatedVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="flex gap-3 group rounded-lg p-2 hover:bg-background-100 transition-colors"
                      >
                        <div className="w-28 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={v.thumbnail}
                            alt={v.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {v.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center gap-0.5">
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                              </div>
                              <span className="text-xs text-foreground-600">{v.rating}</span>
                            </div>
                            <span className="text-xs text-foreground-400">{v.views}次播放</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  <div className="mt-6 p-4 rounded-xl bg-accent-50 border border-accent-200/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-lightbulb-line text-accent-600"></i>
                      </div>
                      <span className="text-xs font-semibold text-accent-800 whitespace-nowrap">观影提示</span>
                    </div>
                    <p className="text-xs text-accent-700 leading-relaxed">
                      所有视频无需注册即可免费观看，支持高清画质播放。建议在WiFi环境下观看以获得最佳体验。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: video.title,
          description: video.description,
          datePublished: `${video.year}-01-01`,
          author: {
            '@type': 'Organization',
            name: '星空传媒',
          },
          about: video.tags.map((tag) => tag).join(', '),
        })}
      </script>
    </>
  );
}